<script>
  import { PoliciesRepository } from "../repositories/index";
  import {
    Button,
    Form,
    TextField,
    Submit,
    Flex,
    FlexItem,
    Modal,
  } from "@gl/design-system";
  import { _ } from "../stores/index";
  import { isValidName, isValidDescription } from "../utils/validators";
  import { createEventDispatcher, onMount } from "svelte";
  import { ModalType } from "../types/modal";
  const dispatch = createEventDispatcher();

  export let appID;
  export let active;
  export let policies;
  export let appType;
  export let modalType;
  export let selectedPolicy;

  const loading = {};

  const form = {};

  function isValidImplicitPolicy(value) {
    if (value === "") return true;
    if (value.includes(" "))
      return [false, $_("policies.implicit.validation.error")];
    const policiesArray = value.split(",");
    for (const policy of policiesArray) {
      const regexTest =
        /^[a-zA-Z0-9]+(_[a-zA-Z0-9]+)*(,[a-zA-Z0-9]+(_[a-zA-Z0-9]+)*)*$/.test(
          policy,
        );
      if (!regexTest) return [false, $_("policies.implicit.validation.error")];
    }
  }

  function mapExternalPolicies(policy) {
    const obj = {
      obj: policy.obj,
      act: policy.act,
    };

    if (policy.description) {
      obj.description = policy.description;
    }

    if (policy.implicit) {
      obj.implicit = policy.implicit.split(",");
    }

    return obj;
  }

  async function submitExternal() {
    loading.submit = true;

    try {
      const requestBody = {
        obj: form.obj,
        act: form.act,
        description: form.description ?? "",
      };

      if (form.implicit) requestBody.implicit = form.implicit.split(",");

      if (modalType === ModalType.Put) {
        const filteredPolicies = policies.filter(
          (p) => p.id !== selectedPolicy.id,
        );
        await PoliciesRepository.updateAppPolicies(appID, [
          ...filteredPolicies.map((p) => mapExternalPolicies(p)),
          requestBody,
        ]);
      } else {
        await PoliciesRepository.updateAppPolicies(appID, [
          ...policies.map((p) => mapExternalPolicies(p)),
          requestBody,
        ]);
      }
      dispatch("success");
      dispatch("close");
    } finally {
      loading.submit = false;
    }
  }

  async function submitInternal() {
    loading.submit = true;

    try {
      if (modalType === ModalType.Put) {
        await PoliciesRepository.patchPolicy(appID, selectedPolicy.id, {
          description: form.description ?? "",
          implicit: form.implicit ? form.implicit.split(",") : null,
        });
      } else {
        await PoliciesRepository.addPolicy(appID, {
          obj: form.obj,
          act: form.act,
          description: form.description ?? "",
          implicit: form.implicit ? form.implicit.split(",") : null,
        });
      }

      dispatch("success");
      dispatch("close");
    } finally {
      loading.submit = false;
    }
  }

  onMount(() => {
    if (selectedPolicy) {
      form.obj = selectedPolicy.obj;
      form.act = selectedPolicy.act;
      form.implicit = selectedPolicy.implicit;
      form.description = selectedPolicy.description;
    }
  });
</script>

<Modal bind:active on:close boxed>
  <div class="gl-title mb-4">
    {modalType === ModalType.Post
      ? $_("policies.add_policy")
      : $_("policies.edit_policy")}
  </div>
  <Form>
    <TextField
      bind:value={form.obj}
      id="policy-obj"
      label={$_("common.obj")}
      required={$_("validations.required")}
      validate={(value) => isValidName(value)}
      disabled={modalType === ModalType.Put}
      maxLength={50}
      config={{ characterCount: $_("character.count") }}
    />

    <TextField
      bind:value={form.act}
      id="policy-act"
      label={$_("common.act")}
      required={$_("validations.required")}
      validate={(value) => isValidName(value)}
      disabled={modalType === ModalType.Put}
      maxLength={50}
      config={{ characterCount: $_("character.count") }}
    />

    <p class="mb-4">
      <b>Name:</b>
      {form.obj ?? ""}_{form.act ?? ""}
    </p>

    <TextField
      bind:value={form.implicit}
      id="policy-implicit"
      label={$_("policies.implicit")}
      validate={(value) => isValidImplicitPolicy(value)}
    />

    <TextField
      bind:value={form.description}
      id="policy-description"
      label={$_("common.description")}
      validate={(value) => isValidDescription(value)}
    />

    <Flex>
      <FlexItem>
        <Submit
          loading={loading.submit}
          on:submit={() => {
            appType === "external" ? submitExternal() : submitInternal();
          }}
          label={$_("buttons.save")}
        />
      </FlexItem>
      <FlexItem class="ml-4">
        <Button outlined on:click={() => dispatch("close")}
          >{$_("buttons.cancel")}</Button
        >
      </FlexItem>
    </Flex>
  </Form>
</Modal>
